// src/store/CompanySlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { supabase } from "services/supabase";
import moment from "moment";

// ------------------------------------------------------------------------
// Initial state
const initialState = {
  loading: false,
  error: null,
  company: {
    companyName: "",
    companyAddress: "",
    companyPhone: "",
    companyEmail: "",
    companyWebsite: "",
    companyLogoUrl: "",
    invoiceInstructions: "",
    paymentPlatform: "",
    paymentLink: "",
    termsAndConditions: "",
    invoiceFooter: "",
  },
  loaded: false,
};

// ------------------------------------------------------------------------

export const getCompany = createAsyncThunk(
  "company/getCompany",
  async (userId, { rejectWithValue }) => {
    try {
      console.log("getCompany: Fetching company information");

      if (!userId) {
        console.error("getCompany: User not authenticated");
        throw new Error("User not authenticated");
      }

      // Fetch company information from companies table
      const { data, error } = await supabase
        .from("companies")
        .select(
          "company_name, company_address, company_phone, company_email, company_website, company_logo_url, invoice_instructions, payment_platform, payment_link, terms_and_conditions, invoice_footer, attach_hours"
        )
        .eq("uid", userId)
        .maybeSingle();

      if (error) {
        console.error("getCompany: Supabase error", error);
        throw error;
      }

      // Return company data with defaults if not found
      const companyData = {
        companyName: data?.company_name || "",
        companyAddress: data?.company_address || "",
        companyPhone: data?.company_phone || "",
        companyEmail: data?.company_email || "",
        companyWebsite: data?.company_website || "",
        companyLogoUrl: data?.company_logo_url || "",
        invoiceInstructions: data?.invoice_instructions || "",
        paymentPlatform: data?.payment_platform || "",
        paymentLink: data?.payment_link || "",
        termsAndConditions: data?.terms_and_conditions || "",
        invoiceFooter: data?.invoice_footer || "",
        attachHours: data?.attach_hours || "yes",
      };

      console.log("getCompany: Company data retrieved", companyData);
      return companyData;
    } catch (error) {
      console.error("getCompany: Caught error", error);
      return rejectWithValue(error.message || error.toString());
    }
  }
);

export const updateCompany = createAsyncThunk(
  "company/updateCompany",
  async ({ userId, ...companyData }, { rejectWithValue }) => {
    try {
      console.log("updateCompany: Updating company information", companyData);

      if (!userId) {
        console.error("updateCompany: User not authenticated");
        throw new Error("User not authenticated");
      }

      // Check if company record exists
      let { data: existingData, error: checkError } = await supabase
        .from("companies")
        .select("id")
        .eq("uid", userId)
        .maybeSingle();

      if (checkError) {
        console.error(
          "updateCompany: Error checking existing company record",
          checkError
        );
        throw checkError;
      }

      let updatedData;
      if (existingData) {
        // Update existing company record
        const { data: updateResult, error } = await supabase
          .from("companies")
          .update({
            company_name: companyData.companyName,
            company_address: companyData.companyAddress,
            company_phone: companyData.companyPhone,
            company_email: companyData.companyEmail,
            company_website: companyData.companyWebsite,
            company_logo_url: companyData.companyLogoUrl,
            invoice_instructions: companyData.invoiceInstructions,
            payment_platform: companyData.paymentPlatform,
            payment_link: companyData.paymentLink,
            terms_and_conditions: companyData.termsAndConditions,
            invoice_footer: companyData.invoiceFooter,
            attach_hours: companyData.attachHours,
            updated_at: moment().toISOString(),
          })
          .eq("id", existingData.id)
          .select(
            "company_name, company_address, company_phone, company_email, company_website, company_logo_url, invoice_instructions, payment_platform, payment_link, terms_and_conditions, invoice_footer, attach_hours"
          )
          .single();

        if (error) {
          console.error("updateCompany: Supabase update error", error);
          throw error;
        }
        updatedData = updateResult;
      } else {
        // Create new company record
        const { data: insertResult, error } = await supabase
          .from("companies")
          .insert({
            uid: userId,
            company_name: companyData.companyName,
            company_address: companyData.companyAddress,
            company_phone: companyData.companyPhone,
            company_email: companyData.companyEmail,
            company_website: companyData.companyWebsite,
            company_logo_url: companyData.companyLogoUrl,
            invoice_instructions: companyData.invoiceInstructions,
            payment_platform: companyData.paymentPlatform,
            payment_link: companyData.paymentLink,
            terms_and_conditions: companyData.termsAndConditions,
            invoice_footer: companyData.invoiceFooter,
            attach_hours: companyData.attachHours,
            updated_at: moment().toISOString(),
          })
          .select(
            "company_name, company_address, company_phone, company_email, company_website, company_logo_url, invoice_instructions, payment_platform, payment_link, terms_and_conditions, invoice_footer, attach_hours"
          )
          .single();

        if (error) {
          console.error("updateCompany: Supabase insert error", error);
          throw error;
        }
        updatedData = insertResult;
      }

      console.log("updateCompany: Company updated successfully", updatedData);
      return updatedData;
    } catch (error) {
      console.error("updateCompany: Caught error", error);
      return rejectWithValue(error.message || error.toString());
    }
  }
);

export const getPublicCompany = createAsyncThunk(
  "company/getPublicCompany",
  async (invoiceId, { rejectWithValue }) => {
    try {
      console.log(
        "getPublicCompany: Fetching company information for invoice",
        invoiceId
      );

      // First verify the invoice exists and get the user ID
      const { data: invoice, error: invoiceError } = await supabase
        .from("invoices")
        .select("uid")
        .eq("id", invoiceId)
        .single();

      if (invoiceError || !invoice) {
        console.error("getPublicCompany: Invoice not found", invoiceError);
        throw new Error("Invoice not found");
      }

      // Fetch company information for the invoice owner
      const { data, error } = await supabase
        .from("companies")
        .select(
          "company_name, company_address, company_phone, company_email, company_website, company_logo_url, invoice_instructions, payment_platform, payment_link, terms_and_conditions, invoice_footer"
        )
        .eq("uid", invoice.uid)
        .maybeSingle();

      if (error) {
        console.error("getPublicCompany: Supabase error", error);
        throw error;
      }

      // Return company data with defaults if not found
      const companyData = {
        companyName: data?.company_name || "",
        companyAddress: data?.company_address || "",
        companyPhone: data?.company_phone || "",
        companyEmail: data?.company_email || "",
        companyWebsite: data?.company_website || "",
        companyLogoUrl: data?.company_logo_url || "",
        invoiceInstructions: data?.invoice_instructions || "",
        paymentPlatform: data?.payment_platform || "",
        paymentLink: data?.payment_link || "",
        termsAndConditions: data?.terms_and_conditions || "",
        invoiceFooter: data?.invoice_footer || "",
      };

      console.log("getPublicCompany: Company data retrieved", companyData);
      return companyData;
    } catch (error) {
      console.error("getPublicCompany: Caught error", error);
      return rejectWithValue(error.message || error.toString());
    }
  }
);

// ------------------------------------------------------------------------
// Slice

export const CompanySlice = createSlice({
  name: "company",
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    clearCompany: (state) => {
      state.company = initialState.company;
      state.loaded = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // ------------------------------------------------------------
      .addCase(getCompany.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCompany.fulfilled, (state, action) => {
        state.company = action.payload;
        state.loading = false;
        state.loaded = true;
        state.error = null;
      })
      .addCase(getCompany.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // ------------------------------------------------------------
      .addCase(updateCompany.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCompany.fulfilled, (state, action) => {
        const data = action.payload;
        // Transform snake_case to camelCase to match state structure
        state.company = {
          companyName: data?.company_name || "",
          companyAddress: data?.company_address || "",
          companyPhone: data?.company_phone || "",
          companyEmail: data?.company_email || "",
          companyWebsite: data?.company_website || "",
          companyLogoUrl: data?.company_logo_url || "",
          invoiceInstructions: data?.invoice_instructions || "",
          paymentPlatform: data?.payment_platform || "",
          paymentLink: data?.payment_link || "",
          termsAndConditions: data?.terms_and_conditions || "",
          invoiceFooter: data?.invoice_footer || "",
        };
        state.loading = false;
        state.error = null;
      })
      .addCase(updateCompany.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // ------------------------------------------------------------
      .addCase(getPublicCompany.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPublicCompany.fulfilled, (state, action) => {
        state.company = action.payload;
        state.loading = false;
        state.loaded = true;
        state.error = null;
      })
      .addCase(getPublicCompany.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

// Export actions and reducers
export const { setLoading, setError, clearCompany } = CompanySlice.actions;

// Selectors
export const selectCompany = (state) => state.companyReducer.company;
export const selectCompanyLoaded = (state) => state.companyReducer.loaded;
export const selectCompanyLoading = (state) => state.companyReducer.loading;
export const selectCompanyError = (state) => state.companyReducer.error;

// ------------------------------------------------------------------------

export default CompanySlice.reducer;
